import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import { TREATMENT_SESSION_APPOINTMENT_TYPE_NAMES } from '~/lib/constants/scheduling.constants';

export enum ProviderAssignmentSelectionTab {
  STRONG = 'Strong match',
  REASONABLE = 'Reasonable match',
  LOW = 'Low match',
}

export enum ActionBarMessage {
  CRITICAL_DATA_MISSING = 'CRITICAL_DATA_MISSING',
  SUBMIT_ERROR = 'SUBMIT_ERROR',
  NO_RECOMMENDATIONS = 'NO_RECOMMENDATIONS',
  NO_AVAILABILITY = 'NO_AVAILABILITY',
  IQ_ERROR = 'IQ_ERROR',
  FOLLOW_UP_WINDOW_EXPIRED = 'FOLLOW_UP_WINDOW_EXPIRED',
}
/**
  Defines the default appointment type to show for each provider type
    - The left side must be a CareTeamProviderType value
    - The right side must be a valid AppointmentType (see /store/services/schedule.ts > getAppointmentTypes)
    - Set the appointmentGroup explicitly to ensure that the correct required / optional attendee permissions are retrieved from schedule service
 */
export const defaultIntakeAppointmentTypes: Partial<
  Record<CareTeamProviderType, Partial<AppointmentType>>
> = {
  [CareTeamProviderType.FAMILY_MENTOR]: {
    appointmentGroup: 'FAMILY_MENTOR_INTAKE',
    appointmentType: 'Family Mentor Intake',
    appointmentTypeLabel: 'Family Mentor Intake, 25 minutes',
    durationInMinutes: 25,
  },
  [CareTeamProviderType.PEER_MENTOR]: {
    appointmentGroup: 'PEER_MENTOR_INTAKE',
    appointmentType: 'Peer Mentor Intake',
    appointmentTypeLabel: 'Peer Mentor Intake, 25 minutes',
    durationInMinutes: 25,
  },
  [CareTeamProviderType.RECOVERY_COACH]: {
    appointmentGroup: 'PEER_MENTOR_INTAKE',
    appointmentType: 'Recovery Coach Intake',
    appointmentTypeLabel: 'Recovery Coach Intake, 25 minutes',
    durationInMinutes: 25,
  },
  [CareTeamProviderType.CAREGIVER_COACH]: {
    appointmentGroup: 'FAMILY_MENTOR_INTAKE',
    appointmentType: 'Caregiver Coach Intake',
    appointmentTypeLabel: 'Caregiver Coach Intake, 25 minutes',
    durationInMinutes: 25,
  },
  [CareTeamProviderType.MEDICAL_PROVIDER]: {
    appointmentGroup: 'MEDICAL_INTAKE',
    appointmentType: 'Medical Intake',
    appointmentTypeLabel: 'Medical Intake, 50 minutes',
    durationInMinutes: 50,
  },
  [CareTeamProviderType.THERAPIST]: {
    appointmentGroup: 'THERAPY_INTAKE',
    appointmentType: 'Therapy Intake',
    appointmentTypeLabel: 'Therapy Intake',
    durationInMinutes: 80,
  },
  [CareTeamProviderType.DIETITIAN]: {
    appointmentGroup: 'DIETITIAN_INTAKE',
    appointmentType: 'Dietitian Intake',
    appointmentTypeLabel: 'Dietitian Intake',
    durationInMinutes: 50,
  },
  [CareTeamProviderType.CARE_NAVIGATOR]: {
    appointmentGroup: 'CARE_NAVIGATOR_INTAKE',
    appointmentType: 'Care Navigator Intake',
    appointmentTypeLabel: 'Care Navigator Intake, 25 minutes',
    durationInMinutes: 25,
  },
};

export const alternateIntakeAppointmentTypes: Partial<
  Record<CareTeamProviderType, Partial<AppointmentType>[]>
> = {
  [CareTeamProviderType.FAMILY_MENTOR]: [
    {
      appointmentGroup: 'FAMILY_MENTOR_INTAKE',
      appointmentType: 'Family Mentor Intake',
      appointmentTypeLabel: 'Family Mentor Intake',
      durationInMinutes: 50,
    },
  ],
  [CareTeamProviderType.PEER_MENTOR]: [
    {
      appointmentGroup: 'PEER_MENTOR_INTAKE',
      appointmentType: 'Peer Mentor Intake',
      appointmentTypeLabel: 'Peer Mentor Intake',
      durationInMinutes: 50,
    },
  ],
  [CareTeamProviderType.RECOVERY_COACH]: [
    {
      appointmentGroup: 'PEER_MENTOR_INTAKE',
      appointmentType: 'Recovery Coach Intake',
      appointmentTypeLabel: 'Recovery Coach Intake',
      durationInMinutes: 50,
    },
  ],
  [CareTeamProviderType.CAREGIVER_COACH]: [
    {
      appointmentGroup: 'FAMILY_MENTOR_INTAKE',
      appointmentType: 'Caregiver Coach Intake',
      appointmentTypeLabel: 'Caregiver Coach Intake',
      durationInMinutes: 50,
    },
  ],
  [CareTeamProviderType.MEDICAL_PROVIDER]: [
    {
      appointmentGroup: 'MEDICAL_INTAKE',
      appointmentType: 'Medical Intake',
      appointmentTypeLabel: 'Medical Intake, 80 minutes',
      durationInMinutes: 80,
    },
  ],
  [CareTeamProviderType.CARE_NAVIGATOR]: [
    {
      appointmentGroup: 'CARE_NAVIGATOR_INTAKE',
      appointmentType: 'Care Navigator Intake',
      appointmentTypeLabel: 'Care Navigator Intake',
      durationInMinutes: 50,
    },
  ],
};

export const AVAILABILITY_WINDOW_WEEKS = 4;

export const FOLLOW_UP_TOTAL_WEEKS = 6;

export const MIN_AVAILABILITY_BUFFER_IN_HOURS = 2;

export const CONFIRMATION_SUBTITLE_TEXT =
  "Please review the patient's schedule. Finalizing your selection will assign the provider to the care team and schedule relevant appointments.";

export const PROVIDER_TEAM_SECTION_LABEL = 'Provider team';
export const APPOINTMENTS_SECTION_LABEL = 'Appointments';

export const PROPOSED_BADGE_TEXT = 'Proposed';

export const CONFIRMATION_STEP_TEST_ID =
  'provider-assignment__confirmation-step';
export const PROVIDER_LIST_TEST_ID = 'confirmation-step__provider-list';
export const APPOINTMENT_LIST_TEST_ID = 'confirmation-step__appointment-list';

export const MAX_PROVIDERS_FOR_AVAILABILITY_FETCH = 20;

export const AVAILABILITY_TOOLTIP_MESSAGE = `Time slots include intake availability up to ${AVAILABILITY_WINDOW_WEEKS} weeks after the admit date, limited to the ${MAX_PROVIDERS_FOR_AVAILABILITY_FETCH} strongest matched providers`;

export const PREFERENCE_WINDOWS: Record<string, { startHour: number; endHour: number }> = {
  '8am-11am': { startHour: 8, endHour: 11 },
  '11am-2pm': { startHour: 11, endHour: 14 },
  '2pm-5pm': { startHour: 14, endHour: 17 },
  '5pm-8pm': { startHour: 17, endHour: 20 },
};

export const RECURRING_SESSION_DURATIONS = new Set([25, 50]);

export const RECURRING_SESSION_TYPES: Set<string> = new Set(
  TREATMENT_SESSION_APPOINTMENT_TYPE_NAMES,
);

export const DEFAULT_RECURRING_DURATION: Partial<
  Record<CareTeamProviderType, number>
> = {
  [CareTeamProviderType.THERAPIST]: 50,
  [CareTeamProviderType.DIETITIAN]: 50,
};

export const DEFAULT_RECURRING_DURATION_FALLBACK = 50;

export const defaultRecurringAppointmentGroups: Partial<
  Record<CareTeamProviderType, string>
> = {
  [CareTeamProviderType.THERAPIST]: 'THERAPY',
  [CareTeamProviderType.DIETITIAN]: 'DIETITIAN',
  [CareTeamProviderType.PEER_MENTOR]: 'PEER_MENTOR',
  [CareTeamProviderType.FAMILY_MENTOR]: 'FAMILY_MENTOR',
  [CareTeamProviderType.RECOVERY_COACH]: 'PEER_MENTOR',
  [CareTeamProviderType.CAREGIVER_COACH]: 'FAMILY_MENTOR',
  [CareTeamProviderType.CARE_NAVIGATOR]: 'RECOVERY_NAVIGATOR',
};

export const FOLLOW_UP_VISIBLE_CHIPS = 4;

export const SELECTED_BADGE_TEXT = 'Selected';

export const PROPOSED_APPOINTMENTS_SECTION_LABEL = 'Proposed Appointments';
